import {
    CheckCircleFilled,
    ClockCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleFilled,
    EyeOutlined,
    FileTextOutlined,
    LoadingOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { Popconfirm } from 'antd';
import classNames from 'classnames';
import { Presentation } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { pptApi } from '../../pptStudio/api';
import { progressPercent } from '../../pptStudio/helpers';
import {
    QUERY_TYPE_LABEL,
    RECENT_ORDERS_EVENT,
    clearRememberedOrders,
    queryOrder,
    rememberedOrders,
} from '../api';
import styles from './index.module.scss';

/** 首页最多展示的记录数 */
const MAX_ROWS = 10;
/** 有订单还在生成时的自动刷新间隔 */
const REFRESH_MS = 30000;
/** 整篇解锁（可在线编辑）的购买方式 */
const FULL_UNLOCK = ['FULL', 'CHAPTER_TO_FULL'];

const clampPercent = (value) => Math.max(0, Math.min(100, Math.round(Number(value) || 0)));

/** 新版写作订单（/ai/order/query）→ 行状态 */
const statusFromWriting = (data) => {
    const paid = !!data?.purchaseMode && data.purchaseMode !== 'UNPAID';
    if (!paid) return { tone: 'muted', text: '待支付', paid: false };
    const docs = Array.isArray(data.documents) ? data.documents : [];
    const main = docs.find((doc) => doc.kind === 'MAIN');
    const generation = data.generationStatus;
    if (generation === 'COMPLETE') {
        return {
            tone: 'done',
            text: '已完成',
            paid: true,
            editOrderNo:
                data.orderNo && FULL_UNLOCK.includes(data.purchaseMode) && (!main || main.status === 'DONE')
                    ? data.orderNo
                    : '',
        };
    }
    if (generation === 'PARTIAL_FAILURE' || generation === 'STOPPED') {
        return { tone: 'warn', text: '生成中断', paid: true };
    }
    return { tone: 'active', text: `生成中 ${clampPercent(data.progress)}%`, paid: true };
};

/** 答辩PPT 任务（/ai/pptgen/tasks/:uid）→ 行状态 */
const statusFromPpt = (view) => {
    const status = view?.status;
    if (status === 'COMPLETED') return { tone: 'done', text: '已完成', paid: true };
    if (status === 'FAILED' || status === 'SUBMIT_FAILED' || status === 'MATERIAL_FAILED') {
        return { tone: 'warn', text: '生成中断', paid: true };
    }
    if (status === 'AWAITING_PAYMENT' && !view?.payment?.paid) {
        return { tone: 'muted', text: '待支付', paid: false };
    }
    return { tone: 'active', text: `生成中 ${clampPercent(progressPercent(view))}%`, paid: true };
};

/** 接口查不到 / 失败时按本机记住的付款状态兜底 */
const fallbackStatus = (record) => {
    if (record.status === 'UNPAID') return { tone: 'muted', text: '待支付', paid: false, stale: true };
    if (record.status === 'PAID') return { tone: 'active', text: '生成中', paid: true, stale: true };
    return { tone: 'muted', text: '状态未知', paid: null, stale: true };
};

const fetchStatus = async (record) => {
    try {
        if (record.kind === 'PPT') {
            if (!record.uid) return fallbackStatus(record);
            const view = await pptApi.task(record.uid, { quiet: true });
            return view ? statusFromPpt(view) : fallbackStatus(record);
        }
        const data = await queryOrder(record.no);
        return data ? statusFromWriting(data) : fallbackStatus(record);
    } catch {
        return fallbackStatus(record);
    }
};

const pad = (n) => String(n).padStart(2, '0');

const formatTime = (ts) => {
    const date = new Date(Number(ts) || 0);
    if (!ts || Number.isNaN(date.getTime())) return '';
    const sameYear = date.getFullYear() === new Date().getFullYear();
    const day = `${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    return `${sameYear ? day : `${date.getFullYear()}-${day}`} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** 同一订单只留一条（uid 优先），最多 MAX_ROWS 条 */
const dedupe = (list) => {
    const seen = new Set();
    return list
        .filter((item) => {
            const key = item.uid || item.no;
            if (!item.no || seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        .slice(0, MAX_ROWS);
};

function StatusPill({ status }) {
    if (!status) {
        return (
            <span className={classNames(styles.status, styles.status_loading)}>
                <LoadingOutlined /> 查询中
            </span>
        );
    }
    return (
        <span className={classNames(styles.status, styles[`status_${status.tone}`])}>
            {status.tone === 'active' ? <LoadingOutlined /> : null}
            {status.tone === 'done' ? <CheckCircleFilled /> : null}
            {status.tone === 'warn' ? <ExclamationCircleFilled /> : null}
            {status.tone === 'muted' ? <ClockCircleOutlined /> : null}
            {status.text}
        </span>
    );
}

/**
 * 首页「我的生成记录」：本机创建 / 付款 / 兑换过的写作与答辩PPT订单，
 * 直接列在写作表单下方，不用再去订单查询里找。没有记录时不渲染。
 */
export default function RecentOrders({ className }) {
    const [records, setRecords] = useState(() => dedupe(rememberedOrders()));
    const [statuses, setStatuses] = useState({});
    const [refreshing, setRefreshing] = useState(false);
    const runRef = useRef(0);

    const reload = useCallback(() => setRecords(dedupe(rememberedOrders())), []);

    // 新记录写入（下单 / 付款 / 兑换）或别的标签页改了记录时刷新
    useEffect(() => {
        window.addEventListener(RECENT_ORDERS_EVENT, reload);
        window.addEventListener('storage', reload);
        return () => {
            window.removeEventListener(RECENT_ORDERS_EVENT, reload);
            window.removeEventListener('storage', reload);
        };
    }, [reload]);

    const recordsKey = records.map((item) => `${item.kind || 'WRITING'}:${item.uid || ''}:${item.no}`).join('|');

    // 并行查询每条记录的最新状态，单条失败不影响其它
    const refresh = useCallback(async () => {
        const runId = ++runRef.current;
        const list = dedupe(rememberedOrders());
        if (!list.length) return;
        setRefreshing(true);
        try {
            const results = await Promise.all(
                list.map(async (record) => [record.no, await fetchStatus(record)]),
            );
            if (runId !== runRef.current) return;
            setStatuses((prev) => {
                const next = { ...prev };
                results.forEach(([no, status]) => {
                    next[no] = status;
                });
                return next;
            });
        } finally {
            if (runId === runRef.current) setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        if (!recordsKey) return undefined;
        refresh();
        return () => {
            runRef.current += 1;
        };
    }, [recordsKey, refresh]);

    const anyActive = records.some((item) => statuses[item.no]?.tone === 'active' && !statuses[item.no]?.stale);
    useEffect(() => {
        if (!anyActive) return undefined;
        const timer = setInterval(() => {
            if (document.visibilityState === 'visible') refresh();
        }, REFRESH_MS);
        return () => clearInterval(timer);
    }, [anyActive, refresh]);

    // 只展示付过款的：接口查到状态以接口为准，没查到时按本机记住的状态；待支付的订单不进列表
    const rows = useMemo(
        () =>
            records
                .filter((record) => {
                    const status = statuses[record.no];
                    return status ? status.paid !== false : record.status !== 'UNPAID';
                })
                .map((record) => {
                const status = statuses[record.no];
                const isPpt = record.kind === 'PPT';
                const resultNo = encodeURIComponent(record.no);
                return {
                    record,
                    status,
                    isPpt,
                    typeLabel: isPpt ? '答辩PPT' : QUERY_TYPE_LABEL[record.queryType] || '写作',
                    resultHref: isPpt
                        ? `/ppt?task=${encodeURIComponent(record.uid || record.no)}`
                        : `/order/result?no=${resultNo}`,
                    previewHref: `/order/preview?no=${resultNo}`,
                    editHref: status?.editOrderNo
                        ? `/editor-v2?orderNo=${encodeURIComponent(status.editOrderNo)}`
                        : '',
                    time: formatTime(record.updatedAt || record.createdAt),
                };
            }),
        [records, statuses],
    );

    if (!rows.length) return null;

    return (
        <section className={classNames(styles.section, className)} aria-label="我的生成记录">
            <div className={styles.header}>
                <div className={styles.headText}>
                    <h2 className={styles.title}>我的生成记录</h2>
                    <p className={styles.subtitle}>本机付款或兑换过的订单，点开即可查看进度和结果；换设备请凭订单号查询</p>
                </div>
                <div className={styles.headActions}>
                    <button
                        type="button"
                        className={styles.linkBtn}
                        onClick={refresh}
                        disabled={refreshing}
                        aria-label="刷新生成记录"
                    >
                        {refreshing ? <LoadingOutlined /> : <ReloadOutlined />} 刷新
                    </button>
                    <Popconfirm
                        title="清除本机的生成记录？"
                        description="只清除本机列表，不影响订单本身；之后仍可凭订单号查询"
                        okText="清除"
                        cancelText="取消"
                        okButtonProps={{ danger: true }}
                        onConfirm={clearRememberedOrders}
                    >
                        <button type="button" className={classNames(styles.linkBtn, styles.danger)}>
                            <DeleteOutlined /> 清除记录
                        </button>
                    </Popconfirm>
                </div>
            </div>

            <ul className={styles.list}>
                {rows.map(({ record, status, isPpt, typeLabel, resultHref, previewHref, editHref, time }) => (
                    <li key={`${record.kind || 'WRITING'}:${record.no}`} className={styles.row}>
                        <div className={styles.main}>
                            <div className={styles.titleLine}>
                                <span className={styles.kind} data-kind={isPpt ? 'PPT' : 'WRITING'}>
                                    {isPpt ? <Presentation size={12} aria-hidden /> : <FileTextOutlined />}
                                    {typeLabel}
                                </span>
                                <span className={styles.rowTitle} title={record.title || ''}>
                                    {record.title || (isPpt ? '答辩PPT' : '写作订单')}
                                </span>
                            </div>
                            <div className={styles.meta}>
                                <StatusPill status={status} />
                                {time ? <span className={styles.time}>{time}</span> : null}
                                <span className={styles.no} title="订单号">
                                    {record.no}
                                </span>
                            </div>
                        </div>
                        <div className={styles.actions}>
                            <Link className={classNames(styles.btn, styles.btnPrimary)} to={resultHref}>
                                {isPpt ? <Presentation size={13} aria-hidden /> : <FileTextOutlined />}
                                查看结果
                            </Link>
                            {!isPpt && status?.paid !== false ? (
                                <Link className={styles.btn} to={previewHref}>
                                    <EyeOutlined />
                                    预览
                                </Link>
                            ) : null}
                            {editHref ? (
                                <a className={styles.btn} href={editHref}>
                                    <EditOutlined />
                                    在线编辑
                                </a>
                            ) : null}
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    );
}
